import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  type OnDestroy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  BrandRouterLinkDirective,
  FormattedEventFieldComponent,
} from '@principle-theorem/ng-principle-shared';
import {
  NgCommonModule,
  ObservableDataTable,
  TrackByFunctions,
  extendSortingDataAccessor,
  timestampSortingAccessor,
} from '@principle-theorem/ng-shared';
import { BrandLinks } from '@principle-theorem/principle-core';
import {
  type IAppointment,
  type IPatient,
} from '@principle-theorem/principle-core/interfaces';
import {
  DATE_TIME_WITH_YEAR_FORMAT,
  type WithRef,
} from '@principle-theorem/shared';
import { ReplaySubject } from 'rxjs';
import { DepositStatusChipComponent } from '../deposit-status-chip/deposit-status-chip.component';

/**
 * A row of the deposit recovery list. The patient travels with the appointment
 * because chasing a deposit is a conversation with a person, and an appointment
 * document has no patient field — only a parent path.
 */
export interface IAppointmentAwaitingDeposit {
  appointment: WithRef<IAppointment>;
  patient: WithRef<IPatient>;
}

@Component({
  selector: 'pr-appointments-awaiting-deposit-list',
  templateUrl: './appointments-awaiting-deposit-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatTableModule,
    MatTooltipModule,
    BrandRouterLinkDirective,
    DepositStatusChipComponent,
    FormattedEventFieldComponent,
  ],
})
export class AppointmentsAwaitingDepositListComponent implements OnDestroy {
  trackByAppointment =
    TrackByFunctions.ref<IAppointmentAwaitingDeposit>('appointment.ref');
  dataTable: ObservableDataTable<IAppointmentAwaitingDeposit>;
  appointments$ = new ReplaySubject<IAppointmentAwaitingDeposit[]>(1);
  readonly dateFormat = DATE_TIME_WITH_YEAR_FORMAT;
  readonly brandLinks = BrandLinks;

  @Output() recordDeposit = new EventEmitter<IAppointmentAwaitingDeposit>();
  @Output() viewAppointment = new EventEmitter<IAppointmentAwaitingDeposit>();

  @ViewChild(MatSort)
  set tableSort(sort: MatSort) {
    this.dataTable.dataSource.sort = sort;
  }

  @Input()
  set appointments(appointments: IAppointmentAwaitingDeposit[]) {
    if (appointments) {
      this.appointments$.next(appointments);
    }
  }

  constructor() {
    this.dataTable = new ObservableDataTable(this.appointments$);
    this.dataTable.displayColumns = [
      'appointment-date',
      'patient-name',
      'practitioner',
      'treatment',
      'deposit',
      'actions',
    ];
    this.dataTable.dataSource.sortingDataAccessor = extendSortingDataAccessor(
      (data, sortHeaderId) => this._sortingDataAccessor(data, sortHeaderId)
    );
  }

  ngOnDestroy(): void {
    this.dataTable.ngOnDestroy();
  }

  private _sortingDataAccessor(
    data: IAppointmentAwaitingDeposit,
    sortHeaderId: string
  ): string | number | undefined {
    switch (sortHeaderId) {
      case 'appointment-date':
        return data.appointment.event
          ? timestampSortingAccessor(data.appointment.event.from)
          : undefined;
      case 'patient-name':
        return data.patient.name.toLowerCase();
      case 'practitioner':
        return data.appointment.practitioner.name.toLowerCase();
      case 'treatment':
        return data.appointment.treatmentPlan.treatmentStep.name.toLowerCase();
      case 'deposit':
        return data.appointment.deposit?.metadata.amountCents;
      default:
        return;
    }
  }
}
